import { uniq, uniqBy } from "es-toolkit/array";
import {
  isRecord,
  toNumber,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization/primitives";
import { resolveDefaultMetricAggregation } from "@/lib/ai/visualization/data/adapters/metric-aggregation";
import type {
  VizDataset,
  VizDatasetRow,
  VizField,
  VizFieldLabelDomain,
  VizMetricAggregation,
} from "@/lib/ai/visualization/data/schema";
import { splitCamelCase } from "@/lib/ai/visualization/shared/primitives";

export {
  alignCompareTimeseries,
  alignCompareTimeseriesByIndex,
  resolveCompareAlignmentMode,
  resolveCompareDateRanges,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization/compare-timeseries";
export {
  extractArray,
  isRecord,
  pickFirstNonEmpty,
  toNumber,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization/primitives";

export const DEFAULT_DATASET_ID = "dataset";

function normalizeSeparators(value: string): string {
  let result = "";
  let inSeparator = false;
  for (const char of value) {
    if (char === "_" || char === "-" || char === " ") {
      if (!inSeparator) result += " ";
      inSeparator = true;
      continue;
    }
    inSeparator = false;
    result += char;
  }
  return result.trim();
}

function normalizeLabel(name: string): string {
  const spaced = normalizeSeparators(splitCamelCase(name));
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function inferFieldFormat(entry: {
  type: VizField["type"];
  unit?: string;
  format?: VizField["format"];
}): NonNullable<VizField["format"]> {
  if (entry.format) return entry.format;
  if (entry.unit === "percent") return "percent";
  if (entry.unit === "duration") return "duration";
  if (entry.type === "date") return "date";
  if (entry.type === "number") return "number";
  return "string";
}

/**
 * adapter 定義から VizField 配列を構築する。
 *
 * @remarks
 * field key は canonical name のみを持つ。
 * authoring / validation に必要な意味論メタだけを持たせ、
 * field key 自体は `fields.name` を唯一の正本として扱う。
 */
export function buildFields(
  keys: ReadonlyArray<{
    key: string;
    role: VizField["role"];
    type: VizField["type"];
    label?: string;
    aliases?: string[];
    unit?: string;
    format?: VizField["format"];
    aggregation?: VizMetricAggregation;
    sortable?: boolean;
    filterable?: boolean;
    exportable?: boolean;
    defaultVisible?: boolean;
    internal?: boolean;
    labelDomain?: VizFieldLabelDomain;
    lineInterpolation?: VizField["lineInterpolation"];
    metricFamily?: VizField["metricFamily"];
    movingAverageWindowDays?: VizField["movingAverageWindowDays"];
    comparisonRole?: VizField["comparisonRole"];
  }>,
): VizField[] {
  return keys.map((entry) => {
    const inferredFilterable =
      entry.role === "x" || entry.role === "dim" || entry.type !== "number";
    const format = inferFieldFormat(entry);
    const aggregation =
      entry.aggregation ??
      (entry.role === "metric" || entry.type === "number"
        ? resolveDefaultMetricAggregation({
            unit: entry.unit,
            format,
          })
        : undefined);

    return {
      name: entry.key,
      role: entry.role,
      type: entry.type,
      label: entry.label?.trim() || normalizeLabel(entry.key),
      ...(entry.aliases?.length ? { aliases: uniq(entry.aliases) } : null),
      format,
      sortable: entry.sortable ?? true,
      filterable: entry.filterable ?? inferredFilterable,
      exportable: entry.exportable ?? true,
      defaultVisible: entry.defaultVisible ?? true,
      ...(entry.unit ? { unit: entry.unit } : null),
      ...(entry.internal ? { internal: true } : null),
      ...(entry.labelDomain ? { labelDomain: entry.labelDomain } : null),
      ...(aggregation ? { aggregation } : null),
      ...(entry.lineInterpolation
        ? { lineInterpolation: entry.lineInterpolation }
        : null),
      ...(entry.metricFamily ? { metricFamily: entry.metricFamily } : null),
      ...(entry.movingAverageWindowDays
        ? { movingAverageWindowDays: entry.movingAverageWindowDays }
        : null),
      ...(entry.comparisonRole
        ? { comparisonRole: entry.comparisonRole }
        : null),
    };
  });
}

/**
 * table 専用 dataset profile を統一形式で生成する。
 */
export function createTableOnlyProfile(args: {
  datasetFamily: string;
  variant?: string;
}): NonNullable<VizDataset["profile"]> {
  return {
    datasetFamily: args.datasetFamily,
    variant: args.variant ?? "table",
    compareMode: "none",
    supportedViews: ["table"],
  };
}

type ListTableField = Parameters<typeof buildFields>[0][number];
type ListTableDefaults = {
  x?: string;
  y?: readonly string[];
  series?: string;
  category?: string;
  value?: string;
};

/**
 * list 系 tool が返す table-only dataset を統一契約で構築する。
 *
 * @remarks
 * list tool は監査用途が中心のため table profile に固定し、
 * rows が空なら dataset を作らず null を返す。
 */
export function buildListTableDataset(args: {
  id: string;
  datasetFamily: string;
  source: VizDataset["source"];
  rows: VizDatasetRow[];
  fields: readonly ListTableField[];
  defaults?: ListTableDefaults;
  notes?: string;
  variant?: string;
}): VizDataset | null {
  const rows = args.rows;
  if (rows.length === 0) return null;
  const defaults: VizDataset["defaults"] | undefined = args.defaults
    ? {
        x: args.defaults.x,
        y: args.defaults.y ? [...args.defaults.y] : undefined,
        series: args.defaults.series,
        category: args.defaults.category,
        value: args.defaults.value,
      }
    : undefined;

  return buildDataset({
    id: args.id,
    profile: createTableOnlyProfile({
      datasetFamily: args.datasetFamily,
      variant: args.variant,
    }),
    fields: buildFields(args.fields),
    rows,
    defaults,
    source: args.source,
    notes: args.notes,
  });
}

/**
 * list 系 row 正規化で使う文字列表現を生成する。
 *
 * @remarks
 * 表示用の短縮は data 層では行わない。必要なら renderer 側で実施する。
 */
export function toListText(value: unknown): string | null {
  return toString(value);
}

/**
 * 文字列配列を監査向けの連結文字列へ整形する。
 *
 * @remarks
 * 省略記法（`+N more`）や切り詰めは行わず、入力配列の全要素を保持する。
 */
export function toListTextList(
  values: readonly unknown[] | null | undefined,
): string | null {
  if (!Array.isArray(values) || values.length === 0) return null;
  const normalized = values
    .map((value) => toListText(value))
    .filter((value): value is string => Boolean(value));
  if (normalized.length === 0) return null;
  return normalized.join(", ");
}

/**
 * boolean 値を table 表示向けの `"true" | "false"` へ変換する。
 */
export function toListBoolean(value: unknown): "true" | "false" | null {
  if (value === true) return "true";
  if (value === false) return "false";
  return null;
}

/**
 * number 値を有限値に限定して返す。
 */
export function toListNumber(value: unknown): number | null {
  return toNumber(value);
}

/**
 * Percent values are represented as ratios (0..1) for consistent formatting.
 * Some tool outputs may provide percent in 0..100; normalize those here.
 *
 * @remarks
 * 見た目の丸めではなく単位意味の統一のための変換なので、
 * adapter 境界で早期に正規化して downstream の推論差を防ぐ。
 */
function normalizePercentRows(
  rows: VizDatasetRow[],
  fields: VizField[],
): VizDatasetRow[] {
  const percentKeys = fields
    .filter((field) => field.unit === "percent")
    .map((field) => field.name);
  if (percentKeys.length === 0) return rows;

  let changed = false;
  const normalized = rows.map((row) => {
    let nextRow = row;

    for (const key of percentKeys) {
      const value = row[key];
      if (typeof value !== "number" || !Number.isFinite(value)) continue;

      const nextValue = value > 1 && value <= 100 ? value / 100 : value;
      if (nextValue === value) continue;

      if (nextRow === row) nextRow = { ...row };
      nextRow[key] = nextValue;
      changed = true;
    }

    return nextRow;
  });

  return changed ? normalized : rows;
}

function dedupeFields(fields: VizField[]): VizField[] {
  return uniqBy(fields, (field) => field.name);
}

function sanitizeDefaults(
  defaults: VizDataset["defaults"] | undefined,
  fieldNameSet: Set<string>,
): VizDataset["defaults"] | undefined {
  if (!defaults) return undefined;

  const sanitizeKey = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    return fieldNameSet.has(value) ? value : undefined;
  };

  const sanitizeKeys = (values: string[] | undefined): string[] | undefined => {
    if (!Array.isArray(values)) return undefined;
    const unique = uniq(values.filter((value) => fieldNameSet.has(value)));
    return unique.length > 0 ? unique : undefined;
  };

  const next: VizDataset["defaults"] = {
    x: sanitizeKey(defaults.x),
    y: sanitizeKeys(defaults.y),
    series: sanitizeKey(defaults.series),
    category: sanitizeKey(defaults.category),
    value: sanitizeKey(defaults.value),
  };

  if (!next.x && !next.y && !next.series && !next.category && !next.value) {
    return undefined;
  }

  return next;
}

function normalizeProfile(args: {
  profile: NonNullable<VizDataset["profile"]>;
}): NonNullable<VizDataset["profile"]> {
  const supportedViews = uniq(args.profile.supportedViews).filter(
    (
      view,
    ): view is NonNullable<VizDataset["profile"]>["supportedViews"][number] =>
      view.length > 0,
  );
  const nextSupportedViews: NonNullable<
    VizDataset["profile"]
  >["supportedViews"] = supportedViews.length > 0 ? supportedViews : ["table"];

  return {
    ...args.profile,
    supportedViews: nextSupportedViews,
  };
}

/**
 * adapter 生成値を VizDataset 契約に正規化する共通ビルダー。
 *
 * @remarks
 * percent 正規化と profile の整合を一括適用する。
 * compareMode は `profile.compareMode` を唯一の正本として扱う。
 */
export function buildDataset(args: {
  id: string;
  fields: VizField[];
  rows: VizDatasetRow[];
  defaults?: VizDataset["defaults"];
  source: VizDataset["source"];
  profile: NonNullable<VizDataset["profile"]>;
  notes?: string;
}): VizDataset {
  const fields = dedupeFields(args.fields);
  const rows = normalizePercentRows(args.rows, fields);
  const fieldNameSet = new Set(fields.map((field) => field.name));
  const defaults = sanitizeDefaults(args.defaults, fieldNameSet);
  const normalizedProfile = normalizeProfile({
    profile: args.profile,
  });

  return {
    id: args.id,
    profile: normalizedProfile,
    fields,
    rows,
    defaults,
    source: args.source,
    ...(args.notes ? { notes: args.notes } : null),
  };
}

function mapRecordItems<T>(
  items: unknown[],
  mapper: (item: Record<string, unknown>) => T | null,
): T[] {
  const mapped: T[] = [];
  for (const item of items) {
    if (!isRecord(item)) continue;
    const value = mapper(item);
    if (!value) continue;
    mapped.push(value);
  }
  return mapped;
}

/**
 * 生配列を `mapper` で安全に VizDatasetRow 配列へ変換する。
 */
export function toDatasetRows(
  items: unknown[],
  mapper: (item: Record<string, unknown>) => VizDatasetRow | null,
): VizDatasetRow[] {
  return mapRecordItems(items, mapper);
}
